import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core';

import { DecimalPipe, Location } from '@angular/common';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ErrorHandler,
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router, provideRouter } from '@angular/router';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';

import { DataService } from './app/dashboard/services/data.service';
import { FireStoreService } from './app/dashboard/services/fire-store.service';
import { FunctionApiService } from './app/dashboard/services/function-api.service';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { environment } from './environments/environment';

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, '../assets/i18n/', '.json');
}

if (environment.production) {
  enableProdMode();
}

// Initialize Sentry based on environment configuration
if (environment.sentry.enabled) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.sentry.environment,
    sendDefaultPii: environment.sentry.sendDefaultPii,
    integrations: [
      // 環境別にAPIトレーシング制御
      environment.sentry.tracePropagationTargets.length > 0
        ? Sentry.browserTracingIntegration({
            // Angular specific tracing options
            enableInp: true, // Interaction to Next Paint (Core Web Vitals)
          })
        : Sentry.browserTracingIntegration({
            // Angular specific tracing options
            enableInp: true, // Interaction to Next Paint (Core Web Vitals)
            // APIトレーシングを無効化（fetchとXHRを監視しない）
            shouldCreateSpanForRequest: () => false,
          }),
      Sentry.replayIntegration({
        // Session replay configuration
        maskAllText: !environment.sentry.sendDefaultPii,
        blockAllMedia: true,
      }),
    ],
    // Tracing
    tracesSampleRate: environment.sentry.tracesSampleRate,
    // APIトレーシング有効環境のみtracePropagationTargetsを設定
    tracePropagationTargets: [...environment.sentry.tracePropagationTargets],
    replaysSessionSampleRate: environment.sentry.replaysSessionSampleRate,
    replaysOnErrorSampleRate: environment.sentry.replaysOnErrorSampleRate,
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideStorage(() => getStorage()),
    importProvidersFrom(
      NgxPageScrollCoreModule.forRoot({
        scrollOffset: 300,
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
      MatNativeDateModule,
      MatSnackBarModule,
    ),
    DecimalPipe,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: !environment.production, // 開発環境のみダイアログ表示
        logErrors: true, // 全環境でコンソールログ出力
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      // Sentry.TraceService を注入して初期化
      inject(Sentry.TraceService);
    }),
  ],
}).catch((err) => console.error(err));

Location.stripTrailingSlash = (url: string) => {
  return url;
};
